import { CheckCircle, XCircle, Info, Copy, X } from 'lucide-react'
import { useState } from 'react'

interface NotificationModalProps {
  isOpen: boolean
  title: string
  message: string
  variant: 'success' | 'error' | 'info'
  onClose: () => void
  copyableText?: string
  copyLabel?: string
  details?: string
}

export default function NotificationModal({
  isOpen,
  title,
  message,
  variant,
  onClose,
  copyableText,
  copyLabel,
  details
}: NotificationModalProps): JSX.Element | null {
  const [copied, setCopied] = useState(false)

  if (!isOpen) return null

  const getIcon = () => {
    switch (variant) {
      case 'success':
        return <CheckCircle size={32} />
      case 'error':
        return <XCircle size={32} />
      case 'info':
        return <Info size={32} />
    }
  }

  const getIconClass = () => {
    switch (variant) {
      case 'success':
        return 'notification-modal__icon--success'
      case 'error':
        return 'notification-modal__icon--error'
      case 'info':
        return 'notification-modal__icon--info'
    }
  }

  const handleCopy = async () => {
    if (copyableText) {
      try {
        await navigator.clipboard.writeText(copyableText)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy:', err)
      }
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content notification-modal" onClick={(e) => e.stopPropagation()}>
        <button className="notification-modal__close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className={`notification-modal__icon ${getIconClass()}`}>
          {getIcon()}
        </div>

        <h3>{title}</h3>
        <p>{message}</p>

        {copyableText && (
          <div className="notification-modal__copy">
            <span className="notification-modal__copy-label">{copyLabel || 'Code'}</span>
            <div className="notification-modal__copy-box">
              <code>{copyableText}</code>
              <button
                className="notification-modal__copy-btn"
                onClick={handleCopy}
                title="Copy to clipboard"
              >
                <Copy size={16} />
                {copied && <span className="notification-modal__copied">Copied!</span>}
              </button>
            </div>
          </div>
        )}

        {details && (
          <p className="notification-modal__details">{details}</p>
        )}

        <div className="modal-actions">
          <button className="primary-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  )
}
